import type { ComparablesDataEntry, MarketComparison, ObservationTime, RentSituation, SourceDate } from '@einwert/valuation-api-client'
import { ApproximateEqualsIcon, CaretDoubleDownIcon, CaretDoubleUpIcon, CaretDownIcon, CaretUpIcon, EmptyIcon } from '@phosphor-icons/react'

import { ContentChipProps, ExhaustivenessError, getMonthName, isoDateStringToLocalFormat } from 'ComponentLibrary'
import { ReactNode } from 'react'
import { ComparableDataWithIndex } from 'ClientPortal/src/pages/Assets/AssetDetails/Valuation/ValuationMarketData/Comparables/market-data'
import { TFunction } from 'i18next'

export type RatingValue =
    | 'very_above_average'
    | 'slightly_above_average'
    | 'average'
    | 'slightly_below_average'
    | 'very_below_average'
    | 'unable_to_rate'

export type RatingLabelProps = {
    label: string
    icon: ReactNode
    color: ContentChipProps['color']
}

export const getAssetNumberRatingChipProps = (rating: number, t: TFunction): RatingLabelProps => {
    if (rating >= 4) {
        return { icon: <CaretDoubleUpIcon weight="bold" />, color: 'green', label: t('ratings.veryGood') }
    } else if (rating >= 3.25) {
        return { icon: <CaretUpIcon weight="bold" />, color: 'lime', label: t('ratings.good') }
    } else if (rating >= 2.25) {
        return { icon: <ApproximateEqualsIcon weight="bold" />, color: 'blue', label: t('ratings.average') }
    } else if (rating >= 1.75) {
        return { icon: <CaretDownIcon weight="bold" />, color: 'orange', label: t('ratings.mediocre') }
    }
    return { icon: <CaretDoubleDownIcon weight="bold" />, color: 'red', label: t('ratings.bad') }
}

export const getAssetRatingChipProps = (rating: RentSituation, t: TFunction): RatingLabelProps => {
    if (['excellent', 'very_good'].includes(rating)) {
        return {
            icon: <CaretDoubleUpIcon weight="bold" />,
            color: 'green',
            label: t(rating === 'excellent' ? 'ratings.excellent' : 'ratings.veryGood'),
        }
    } else if (['good', 'above_average', 'slightly_above_average'].includes(rating)) {
        return {
            icon: <CaretUpIcon weight="bold" />,
            color: 'lime',
            label: t(
                rating === 'good' ? 'ratings.good' : rating === 'above_average' ? 'ratings.aboveAverage' : 'ratings.slightlyAboveAverage'
            ),
        }
    } else if (['average', 'slightly_below_average', 'below_average'].includes(rating)) {
        return {
            icon: <ApproximateEqualsIcon weight="bold" />,
            color: 'blue',
            label: t(
                rating === 'average'
                    ? 'ratings.average'
                    : rating === 'below_average'
                      ? 'ratings.belowAverage'
                      : 'ratings.slightlyBelowAverage'
            ),
        }
    } else if (['mediocre', 'bad'].includes(rating)) {
        return { icon: <CaretDownIcon weight="bold" />, color: 'orange', label: t(rating === 'bad' ? 'ratings.bad' : 'ratings.mediocre') }
    }
    return { icon: <CaretDoubleDownIcon weight="bold" />, color: 'red', label: t('ratings.catastrophic') }
}

const MARKET_COMPARISON_RATING_STYLES: Record<RatingValue, Omit<RatingLabelProps, 'label'>> = {
    very_above_average: {
        icon: <CaretDoubleUpIcon />,
        color: 'green',
    },
    slightly_above_average: {
        icon: <CaretUpIcon />,
        color: 'lime',
    },
    average: { icon: <ApproximateEqualsIcon />, color: 'blue' },
    slightly_below_average: {
        icon: <CaretDownIcon />,
        color: 'orange',
    },
    very_below_average: {
        icon: <CaretDoubleDownIcon />,
        color: 'red',
    },
    unable_to_rate: {
        icon: <EmptyIcon />,
        color: 'grey',
    },
}

export const getMarketRatingChipProps = (rating: RatingValue, t: TFunction) => ({
    ...MARKET_COMPARISON_RATING_STYLES[rating],
    label: t(
        rating === 'very_above_average'
            ? 'ratings.veryAboveAverage'
            : rating === 'slightly_above_average'
              ? 'ratings.slightlyAboveAverage'
              : rating === 'average'
                ? 'ratings.average'
                : rating === 'slightly_below_average'
                  ? 'ratings.slightlyBelowAverage'
                  : rating === 'very_below_average'
                    ? 'ratings.veryBelowAverage'
                    : 'ratings.unableToRate'
    ),
})

export const DATA_ENTRY_TYPE_VALUES_BY_MARKET_DATA_TYPE: Record<keyof MarketComparison, ComparablesDataEntry['data_type'][]> = {
    market_value: ['market_area_value', 'market_value'],
    yield_and_multiplier: ['yield', 'multiplier', 'discount_rate'],
    rent: ['monthly_rent', 'yearly_rent'],
}

export const getDataEntryTabsByMarketType = (marketData: ComparableDataWithIndex[], marketDataType: keyof MarketComparison, t: TFunction) =>
    DATA_ENTRY_TYPE_VALUES_BY_MARKET_DATA_TYPE[marketDataType]
        .filter((value) => marketData.some(({ entries }) => entries.some(({ data_type }) => data_type === value)))
        .map((value) => ({ value, label: t(`tabs.${value}`) }))

const UNIT_TRANSLATION_KEYS_BY_DATA_TYPE: Record<ComparablesDataEntry['data_type'], string> = {
    market_value: 'units.market_value',
    market_area_value: 'units.market_area_value',
    yield: 'units.yield',
    multiplier: 'units.multiplier',
    yearly_rent: 'units.yearly_rent',
    monthly_rent: 'units.monthly_rent',
    discount_rate: 'units.discount_rate',
}

export const getUnitByDataType = (dataType: ComparablesDataEntry['data_type'], t: TFunction) =>
    t(UNIT_TRANSLATION_KEYS_BY_DATA_TYPE[dataType])

export const getMarketComparisonTitle = (marketDataType: keyof MarketComparison, t: TFunction) => t(`titles.${marketDataType}`)

const VALUE_LABEL_TRANSLATION_KEYS = {
    absolute: 'valueLabels.absolute',
    average: 'valueLabels.average',
    minimum: 'valueLabels.minimum',
    maximum: 'valueLabels.maximum',
    median: 'valueLabels.median',
    mode: 'valueLabels.mode',
    uppermost_quartile: 'valueLabels.uppermostQuartile',
    lowest_quartile: 'valueLabels.lowestQuartile',
    very_good_residential_value: 'valueLabels.veryGoodResidentialValue',
    good_residential_value: 'valueLabels.goodResidentialValue',
    medium_residential_value: 'valueLabels.mediumResidentialValue',
    bad_residential_value: 'valueLabels.badResidentialValue',
    very_good_usage_value: 'valueLabels.veryGoodUsageValue',
    good_usage_value: 'valueLabels.goodUsageValue',
    medium_usage_value: 'valueLabels.mediumUsageValue',
    bad_usage_value: 'valueLabels.badUsageValue',
} as const

export const getValueLabel = (label: string | undefined, t: TFunction) => {
    if (!label) {
        return ''
    }

    return label in VALUE_LABEL_TRANSLATION_KEYS
        ? t(VALUE_LABEL_TRANSLATION_KEYS[label as keyof typeof VALUE_LABEL_TRANSLATION_KEYS])
        : label
}

export const observationTimeToDescription = (observationTime: ObservationTime | undefined, t: TFunction, locale?: string) => {
    if (!observationTime) {
        return undefined
    }

    return observationTime.time_type === 'date'
        ? t('observationTime.date', { date: isoDateStringToLocalFormat(observationTime.date, false, locale) })
        : t('observationTime.range', { range: observationTime.custom_range || t('observationTime.unknownRange') })
}

export const retrievedOnDateToDescription = (retrievedOn: string | undefined, t: TFunction, locale?: string) => {
    if (!retrievedOn) {
        return undefined
    }

    return t('retrievedOn', { date: isoDateStringToLocalFormat(retrievedOn, false, locale) })
}

export const sourceDateToDateString = (sourceDate?: SourceDate, locale?: string) => {
    if (!sourceDate) {
        return undefined
    }

    switch (sourceDate.date_type) {
        case 'year':
            return sourceDate.year.toString()
        case 'month_year':
            return `${getMonthName(sourceDate.month, locale)} ${sourceDate.year}`
        case 'day_month_year':
            return isoDateStringToLocalFormat(sourceDate.date, false, locale)
        default:
            throw ExhaustivenessError(sourceDate)
    }
}

export const sourceDateToDescription = (sourceDate: SourceDate | undefined, t: TFunction, locale?: string) => {
    if (!sourceDate) {
        return undefined
    }

    const dateString = sourceDateToDateString(sourceDate, locale)
    switch (sourceDate.date_type) {
        case 'year':
            return t('sourceDate.year', { date: dateString })
        case 'month_year':
        case 'day_month_year':
            return t('sourceDate.dated', { date: dateString })
        default:
            throw ExhaustivenessError(sourceDate)
    }
}

export const getAutoConvertedFootNote = (footNote: string | undefined, t: TFunction) =>
    t('autoConvertedFootNote', { footNote: footNote || '-' })
